import React from 'react';
import { Navigate } from 'react-router-dom';
import { Download, Loader2, RefreshCw } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../components/ToastProvider';
import useActivityLog from '../features/admin/activity/useActivityLog';
import ActivityFilters from '../features/admin/activity/ActivityFilters';
import ActivityTable from '../features/admin/activity/ActivityTable';

export default function AdminActivity() {
  const { can, loading: authLoading } = useAuth();
  const canViewAudit = can('view_audit');
  const toast = useToast();

  const log = useActivityLog({ enabled: canViewAudit });

  if (authLoading) {
    return (
      <div className="canvas-inner" style={{ textAlign: 'center', padding: 80 }}>
        <Loader2 className="w-5 h-5 animate-spin" style={{ color: 'var(--icon-secondary)' }} />
      </div>
    );
  }
  if (!canViewAudit) return <Navigate to="/" replace />;

  return (
    <div className="canvas-inner animate-fade-in">
      <div className="page-header">
        <div>
          <div className="page-eyebrow">Admin</div>
          <h1 className="page-title">Activity</h1>
          <div className="page-subtitle">
            Who's using Docket and what they're doing — logins, plus actions and contract access. Admin-only.
          </div>
        </div>
        <div className="row" style={{ gap: 8 }}>
          {/* Filter-aware audit-log export (doc-116): downloads every row
              matching the current filters, not just the visible page. */}
          <button
            className="btn outline sm"
            onClick={() => log.exportCsv().catch((err) => toast.error('Export failed', err.message))}
            disabled={log.exporting || log.loading || log.pagination.total === 0}
            title={
              log.pagination.total === 0
                ? 'Nothing to export — no activity matches the current filters'
                : 'Download the filtered log as CSV'
            }
          >
            <Download className="w-3.5 h-3.5" />
            {log.exporting ? 'Exporting…' : 'Export CSV'}
          </button>
          <button className="btn outline sm" onClick={log.refresh} disabled={log.loading}>
            {log.loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            Refresh
          </button>
        </div>
      </div>

      <ActivityFilters
        q={log.q} setQ={log.setQ}
        userId={log.userId} setUserId={log.setUserId}
        action={log.action} setAction={log.setAction}
        entityType={log.entityType} setEntityType={log.setEntityType}
        range={log.range} setRange={log.setRange}
        customFrom={log.customFrom} setCustomFrom={log.setCustomFrom}
        customTo={log.customTo} setCustomTo={log.setCustomTo}
        errorsOnly={log.errorsOnly} setErrorsOnly={log.setErrorsOnly}
        actions={log.actions} actors={log.actors}
        hasFilters={log.hasFilters} clearFilters={log.clearFilters}
      />

      <ActivityTable
        entries={log.entries}
        loading={log.loading}
        error={log.error}
        page={log.page}
        pageSize={log.pageSize}
        pageSizeOptions={log.pageSizeOptions}
        pagination={log.pagination}
        setPage={log.setPage}
        handlePageSizeChange={log.handlePageSizeChange}
        hasFilters={log.hasFilters}
        clearFilters={log.clearFilters}
      />
    </div>
  );
}
